import React, { useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Sparkles, Crown, AlertCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useInsightsStore } from '../../store/insightsStore';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { Button } from '../ui/Button';
import { HabitInsight } from '../../types';

interface WeeklyInsightsPanelProps {
  onUpgrade: () => void;
}

export const WeeklyInsightsPanel: React.FC<WeeklyInsightsPanelProps> = ({ onUpgrade }) => {
  const { weeklyInsight, isLoading, isGenerating, error, loadWeeklyInsights, generateInsights, clearError } =
    useInsightsStore();
  const { canAccessWeeklyInsights } = useFeatureAccess();

  useEffect(() => {
    if (canAccessWeeklyInsights && !weeklyInsight && !isLoading) {
      loadWeeklyInsights();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessWeeklyInsights]);

  if (!canAccessWeeklyInsights) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl border-2 border-dashed border-purple-300 dark:border-purple-700">
        <div className="flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-4">
          <Crown className="w-8 h-8 text-purple-600 dark:text-purple-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Weekly Insights</h3>
        <p className="text-gray-600 dark:text-gray-400 text-center mb-6 max-w-md">
          Get AI-powered weekly summaries of your habit performance, trends, and personalized encouragement.
          Available on Basic and Pro plans.
        </p>
        <Button onClick={onUpgrade} variant="primary">Upgrade to Basic or Pro</Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="insights-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading your insights…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-start space-x-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 mb-4">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
        <Button onClick={() => { clearError(); generateInsights(); }} variant="outline" size="sm">Try Again</Button>
      </div>
    );
  }

  if (!weeklyInsight) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6">
        <div className="flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-4">
          <Sparkles className="w-8 h-8 text-purple-600 dark:text-purple-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No insights yet</h3>
        <p className="text-gray-600 dark:text-gray-400 text-center mb-6 max-w-sm">
          Generate your first AI-powered weekly summary to see how you're doing.
        </p>
        <Button onClick={generateInsights} variant="primary" isLoading={isGenerating} leftIcon={<Sparkles size={16} />}>
          Generate Insights
        </Button>
      </div>
    );
  }

  const chartData = weeklyInsight.habitInsights.map((h) => ({
    name: h.habitName.length > 10 ? `${h.habitName.slice(0, 10)}…` : h.habitName,
    completions: h.completionsThisWeek,
  }));

  const weekLabel = (() => {
    const start = new Date(weeklyInsight.weekStart);
    const end = new Date(weeklyInsight.weekEnd);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  })();

  return (
    <div className="space-y-6 px-4 pt-2 pb-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
            <Sparkles className="w-5 h-5 text-purple-500" />
            <span>Weekly Insights</span>
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{weekLabel}</p>
        </div>
      </div>

      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-800">
        <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">{weeklyInsight.summary}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
          Generated {new Date(weeklyInsight.generatedAt).toLocaleString()}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Weekly Rate" value={`${weeklyInsight.metrics.weeklyCompletionRate}%`} />
        <MetricCard label="Active Streaks" value={String(weeklyInsight.metrics.activeStreaks)} />
        <MetricCard label="Completions" value={String(weeklyInsight.metrics.totalCompletions)} />
        <MetricCard label="Top Habit" value={weeklyInsight.metrics.topHabitName ?? '—'} small />
      </div>

      {chartData.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
            Completions this week
          </p>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.1} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v: number) => [`${v} completions`]} />
                <Bar dataKey="completions" radius={[4, 4, 0, 0]} fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {weeklyInsight.habitInsights.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">
            Habit Highlights
          </p>
          <div className="space-y-2">
            {weeklyInsight.habitInsights.map((h) => (
              <HabitInsightCard key={h.habitId} insight={h} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard: React.FC<{ label: string; value: string; small?: boolean }> = ({ label, value, small }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{label}</p>
    <p className={`font-semibold text-gray-900 dark:text-gray-100 truncate ${small ? 'text-sm mt-0.5' : 'text-lg'}`}>
      {value}
    </p>
  </div>
);

const HabitInsightCard: React.FC<{ insight: HabitInsight }> = ({ insight }) => {
  const TrendIcon = insight.trend === 'up' ? TrendingUp : insight.trend === 'down' ? TrendingDown : Minus;
  const trendColor = insight.trend === 'up'
    ? 'text-green-600 dark:text-green-400'
    : insight.trend === 'down' ? 'text-red-500 dark:text-red-400' : 'text-gray-400';

  return (
    <div className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold"
        style={{ backgroundColor: insight.colorCode }}
      >
        {insight.habitName.charAt(0)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{insight.habitName}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{insight.highlight}</p>
      </div>
      <div className="flex items-center space-x-2 flex-shrink-0 text-right">
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{insight.completionsThisWeek}/7</p>
          <p className="text-xs text-gray-400">this week</p>
        </div>
        <TrendIcon size={16} className={trendColor} />
      </div>
    </div>
  );
};
